import { DragDropModule } from '@angular/cdk/drag-drop';
import {
  CommonModule,
  DatePipe,
  PercentPipe,
  TitleCasePipe,
} from '@angular/common';
import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ErrorHandler,
  inject,
  NgModule,
  NO_ERRORS_SCHEMA,
  provideAppInitializer,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import {
  BrowserModule,
  REMOVE_STYLES_ON_COMPONENT_DESTROY,
  Title,
} from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { FFlowModule } from '@foblex/flow';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { MtxColorpickerModule } from '@ng-matero/extensions/colorpicker';
import { MtxNativeDatetimeModule } from '@ng-matero/extensions/core';
import { MtxDatetimepickerModule } from '@ng-matero/extensions/datetimepicker';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import {
  ConnectionServiceModule,
  ConnectionServiceOptions,
  ConnectionServiceOptionsToken,
} from 'angular-connection-service';
import { KeyboardShortcutsModule } from 'ng-keyboard-shortcuts';
import { ImageCropperModule } from 'ngx-image-cropper';
import { SocketIoModule } from 'ngx-socket-io';
import { ToastrModule } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs';

import { environment } from '../environments/environment';

// Services
import { AuthService } from './modules/auth/services/auth.service';
// Core Modules
import { ConfigurableFieldsModule } from './modules/configurable-fields/configurable-fields.module';
import { CoreModule } from './modules/core/core.module';
import { BankOrderTemplateComponent } from './modules/payouts/pages/bank-order-template/bank-order-template.component';
import { PayslipTemplateComponent } from './modules/payouts/pages/payslip-template/payslip-template.component';
import { ProposalTemplateComponent } from './modules/policies/components/print-proposal/print-proposal.component';
import { CurrenciesService } from './modules/setup/currencies/services/currencies.service';
import { ExternalSystemsService } from './modules/setup/external-systems/services/external-systems.service';
import { ClientDataService } from './modules/setup/general/services/client-data.service';
import { SystemDataService } from './modules/setup/general/services/system-data.service';
import { IamConfigService } from './modules/setup/iam-config/services/iam-config.service';
import { LanguagesService } from './modules/setup/languages/services/languages.service';
import { MailBoxConfigService } from './modules/setup/mailbox/pages/services/mailbox-config.service';
import { NotificationConfigService } from './modules/setup/notifications/services/notification-config.service';
import { PWAService } from './modules/setup/pwa/services/pwa.service';
import { ReleaseService } from './modules/setup/releases/services/release.service';
import { SecurityService } from './modules/setup/security/services/security.service';
import { StorageSetupService } from './modules/setup/storage/services/storage-setup.service';
import { SupportService } from './modules/setup/support/services/support.service';
import { ProgressToastComponent } from './modules/shared/components/progress.toast/progress-toast.component';
import { SharedModule } from './modules/shared/shared.module';
import { WidgetModule } from './modules/widgets/widget.module';
import { AboutComponent } from './pages/about/about.component';
import { ViewReleaseNoteComponent } from './pages/about/popups/view-release-note/view-release-note.component';
import { AccessDeniedComponent } from './pages/access-denied/access-denied.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { NotificationPanelComponent } from './pages/dashboard/notification-panel/notification-panel.component';
import { MenuItemsComponent } from './pages/dashboard/side-menu/menu-items/menu-items.component';
import { SideMenuComponent } from './pages/dashboard/side-menu/side-menu.component';
import { DeactivateModuleComponent } from './pages/deactivate-module/deactivate-module.component';
import { HomeComponent } from './pages/home/home.component';
import { MaintenanceComponent } from './pages/maintenance/maintenance.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { PersonalizationComponent } from './pages/personalization/personalization.component';
import { CreateNoteComponent } from './pages/post-it-notes/popups/create-note/create-note.component';
import { ConnectionsDataFieldsComponent } from './pages/profile/connections-data-fields/connections-data-fields.component';
import { ImageCropperDialogComponent } from './pages/profile/image-cropper-dialog/image-cropper-dialog.component';
import { ProfileComponent } from './pages/profile/profile.component';
import { AddQuickActionComponent } from './pages/quick-actions/popups/add-quick-action/add-quick-action.component';
import { ResetPasswordComponent } from './pages/reset-password/reset-password.component';
import { GlobalErrorHandlerService } from './services/global-error-handler.service';
// Others
import { AppComponent } from './app.component';
import { AppInitializer } from './app.initializer';
import { AppRoutingModule } from './app-routing.module';
import { MaterialModule } from './material.module';

function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '/api/assets/languages/');
}

function appInitializerFactory(translateService: TranslateService) {
  return () => lastValueFrom(translateService.use('en'));
}

const thirdPartyModules = [
  NgbModule,
  SocketIoModule.forRoot({ url: environment.API_URL }),
  TranslateModule.forRoot({
    loader: {
      provide: TranslateLoader,
      useFactory: HttpLoaderFactory,
      deps: [HttpClient],
    },
  }),
  DragDropModule,
  MtxColorpickerModule,
  ImageCropperModule,
  ToastrModule.forRoot({ maxOpened: 4, preventDuplicates: true }),
  ConnectionServiceModule,
  ServiceWorkerModule.register('ngsw-worker.js', {
    enabled: environment.production,
    registrationStrategy: 'registerImmediately',
  }),
  KeyboardShortcutsModule.forRoot(),
  MtxNativeDatetimeModule,
  MtxDatetimepickerModule,
  CalendarModule.forRoot({
    provide: DateAdapter,
    useFactory: adapterFactory,
  }),
];

@NgModule({
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  bootstrap: [AppComponent],
  declarations: [
    AppComponent,
    DashboardComponent,
    SideMenuComponent,
    MenuItemsComponent,
    NotificationPanelComponent,
    HomeComponent,
    AboutComponent,
    ViewReleaseNoteComponent,
    PersonalizationComponent,
    ProfileComponent,
    ConnectionsDataFieldsComponent,
    ResetPasswordComponent,
    DeactivateModuleComponent,
    AccessDeniedComponent,
    MaintenanceComponent,
    NotFoundComponent,
    ImageCropperDialogComponent,
    AddQuickActionComponent,
    CreateNoteComponent,
    ProposalTemplateComponent,
    PayslipTemplateComponent,
    BankOrderTemplateComponent,
    ProgressToastComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    CoreModule,
    SharedModule,
    MaterialModule,
    ReactiveFormsModule,
    FormsModule,
    CKEditorModule,
    FFlowModule,
    NgxChartsModule,
    WidgetModule,
    ConfigurableFieldsModule,
    Sentry.TraceModule,
    ...thirdPartyModules,
  ],
  providers: [
    provideAnimations(),
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    {
      provide: ConnectionServiceOptionsToken,
      useValue: <ConnectionServiceOptions>{
        enableHeartbeat: true,
        heartbeatUrl: '/api',
        requestMethod: 'get',
        heartbeatInterval: 60000,
        heartbeatRetryInterval: 1000000,
      },
    },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    { provide: REMOVE_STYLES_ON_COMPONENT_DESTROY, useValue: false },
    Title,
    TitleCasePipe,
    PercentPipe,
    DatePipe,
    provideHttpClient(withInterceptorsFromDi()),
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
      return;
    }),
    provideAppInitializer(() => {
      const initializerFn = AppInitializer(
        inject(AuthService),
        inject(MailBoxConfigService),
        inject(ReleaseService),
        inject(SecurityService),
        inject(ClientDataService),
        inject(SystemDataService),
        inject(LanguagesService),
        inject(NotificationConfigService),
        inject(PWAService),
        inject(IamConfigService),
        inject(StorageSetupService),
        inject(SupportService),
        inject(ExternalSystemsService),
        inject(CurrenciesService)
      );
      return initializerFn();
    }),
    provideAppInitializer(() =>
      appInitializerFactory(inject(TranslateService))()
    ),
  ],
})
export class AppModule {}
